// src/components/layout/mobile/MobileSheet.tsx
// Full-screen slide-up sheet for mobile detail views.
// Replaces modals/dialogs on mobile with a full-viewport experience.
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronLeft, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ErrorBoundary } from '@/components/common';

interface MobileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /** Show back arrow instead of X */
  showBack?: boolean;
  /** Right-side header actions */
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function MobileSheet({
  open,
  onOpenChange,
  title,
  showBack = false,
  headerActions,
  children,
  className,
}: MobileSheetProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 z-50 bg-background flex flex-col',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
            'data-[state=closed]:duration-200 data-[state=open]:duration-300',
            className
          )}
        >
          {/* Header */}
          <div className="flex items-center h-12 px-3 border-b border-border shrink-0">
            <button
              onClick={() => onOpenChange(false)}
              className="min-w-touch min-h-touch flex items-center justify-center -ml-1 rounded-md active:bg-muted/50 transition-colors"
              aria-label={showBack ? 'Volver' : 'Cerrar'}
            >
              {showBack ? (
                <ChevronLeft className="h-5 w-5" />
              ) : (
                <X className="h-5 w-5" />
              )}
            </button>

            {title ? (
              <DialogPrimitive.Title className="flex-1 text-sm font-semibold truncate px-2 text-center">
                {title}
              </DialogPrimitive.Title>
            ) : (
              <DialogPrimitive.Title className="sr-only">Detalle</DialogPrimitive.Title>
            )}

            <div className="flex items-center min-w-[40px] justify-end">
              {headerActions}
            </div>
          </div>

          <DialogPrimitive.Description className="sr-only">
            Vista de detalle en pantalla completa
          </DialogPrimitive.Description>

          {/* Scrollable content — ErrorBoundary needed because Radix portals
              render outside the main tree, bypassing MobileLayout's boundary */}
          <div className="flex-1 overflow-y-auto overscroll-y-contain">
            <ErrorBoundary>
              {children}
            </ErrorBoundary>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
